import type { ComponentProps, ReactElement } from 'react'

import { Trans } from '@lingui/react'
import { isNil } from 'ramda'
import type { UseFormReturn } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import { FormHelperText } from '../../atoms/form-helper-text'
import type { InputProps } from '../../atoms/input'
import { Typography } from '../../atoms/typography'

import { useAutocomplete } from './autocomplete.hook'
import { DropDownItemText, DropDownList, DropdownListItem } from './dropdown'
import * as styles from './old-autocomplete.styles'

export type Option<T> = ComponentProps<typeof DropDownItemText> & {
  value: T
  id: string
}

type Props<T> = {
  options: Array<Option<T>>
  onOptionSelect?: (selectedOption: Option<T>) => void
  onInputChange?: (value: string) => void
  getInputValueOnSelect?: (selectedOption: Option<T>) => string
  renderInput: (inputParams: InputProps) => ReactElement
  name: string
  defaultOption?: Option<T>
  initialInputValue?: string
  persistSelection?: boolean
  filterLocally?: boolean
} & Pick<InputProps, 'rules'>

export const AutoComplete = <T extends unknown>({
  options,
  renderInput,
  onOptionSelect,
  onInputChange,
  getInputValueOnSelect,
  name,
  rules,
  defaultOption,
  initialInputValue,
  persistSelection = true,
  filterLocally = true,
}: Props<T>) => {
  const { register, getValues, formState } =
    (useFormContext() as UseFormReturn | null) || {}

  const defaultValue = getValues ? getValues(name) : null
  const filteredDefaultOption = options.find(
    (option) => option.id === defaultValue
  )

  const {
    rootProps,
    filteredOptions,
    renderedInputProps,
    handleOptionSelect,
    openDropdown,
    selectedOption,
  } = useAutocomplete({
    options,
    name,
    onOptionSelect,
    onInputChange,
    getInputValueOnSelect,
    defaultOption: defaultOption ?? filteredDefaultOption,
    initialInputValue,
    persistSelection,
    filterLocally,
  })

  const fieldErrors = formState?.errors[name]

  return (
    <div css={styles.root} data-testid="autocomplete" {...rootProps}>
      {/* in this input we display the primary text */}
      {renderInput({
        ...renderedInputProps,
        error: !isNil(formState?.errors[name]),
      })}
      {/* to this input we store the id of selected suggestion */}
      {persistSelection && (
        <input
          {...register?.(name, {
            ...rules,
          })}
          defaultValue={selectedOption?.id}
          name={name}
          type="hidden"
        />
      )}
      {persistSelection && (
        <FormHelperText
          errors={formState?.errors}
          isError={!isNil(fieldErrors)}
          name={name}
        />
      )}
      <DropDownList opened={openDropdown && !!options.length}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((suggestion) => (
            <DropdownListItem
              key={suggestion.id}
              active={suggestion.id === selectedOption?.id}
              onClick={handleOptionSelect(suggestion)}
            >
              <DropDownItemText {...suggestion} />
            </DropdownListItem>
          ))
        ) : (
          <DropdownListItem key="no-option">
            <Typography color="textSecondary" variant="b3">
              <Trans id="components.molecules.autocomplete.noOptions" />
            </Typography>
          </DropdownListItem>
        )}
      </DropDownList>
    </div>
  )
}
